"use client";

import { useIntl } from "react-intl";
import { FaCircleHalfStroke, FaMoon, FaSun } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import { setPreference, type ThemePreference } from "@/app/store/themeSlice";
import { trackEvent } from "@/app/providers/ObservabilityProvider";

const OPTIONS: { value: ThemePreference; labelId: string; Icon: typeof FaSun }[] = [
  { value: "system", labelId: "theme.system", Icon: FaCircleHalfStroke },
  { value: "light", labelId: "theme.light", Icon: FaSun },
  { value: "dark", labelId: "theme.dark", Icon: FaMoon },
];

/** Footer control: follow the device, or force light / dark. Stored in localStorage. */
export default function ThemeToggle() {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const preference = useAppSelector((s) => s.theme.preference);

  return (
    <div
      role="radiogroup"
      aria-label={intl.formatMessage({ id: "theme.label" })}
      className="inline-flex rounded-full bg-gray-ulight p-1 ring-1 ring-gray-light"
    >
      {OPTIONS.map(({ value, labelId, Icon }) => {
        const on = preference === value;
        const label = intl.formatMessage({ id: labelId });
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={label}
            title={label}
            onClick={() => {
              trackEvent("footer-theme", { theme: value });
              dispatch(setPreference(value));
            }}
            className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-sm transition-colors sm:h-8 sm:w-8 ${
              on ? "bg-blue-main text-white shadow-sm" : "text-gray-main hover:text-title-blue"
            }`}
          >
            <Icon aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
